import { Link } from "react-router-dom";
import { Heart, MessageCircle, Play, Eye, Lock, Layers } from "lucide-react";
import { mediaSrc, prefetchMedia } from "../lib/api";
import { VerifiedBadge } from "./VerifiedBadge";

// Sleek premium media card: full-colour imagery, smooth zoom, a glass caption
// that rises on hover with an electric accent. No theme gimmicks — just clean + cool.
export default function MediaCard({ item, index = 0 }) {
  return (
    <Link
      to={`/media/${item.id}`}
      onMouseEnter={() => prefetchMedia(item.id)}
      onTouchStart={() => prefetchMedia(item.id)}
      data-testid={`media-card-${item.id}`}
      className="relative group block overflow-hidden break-inside-avoid mb-3 sm:mb-4 rounded-2xl bg-[#0B0B0D] ring-1 ring-white/[0.06] hover:ring-[#00E5FF]/50 shadow-[0_12px_34px_-16px_rgba(0,0,0,0.9)] hover:shadow-[0_24px_60px_-20px_rgba(0,160,255,0.35)] hover:-translate-y-1.5 transition-[transform,box-shadow] duration-400 fade-up"
      style={{ animationDelay: `${Math.min(index * 0.03, 0.4)}s` }}
    >
      {item.media_type === "video" ? (
        <video
          src={mediaSrc(item.url)}
          poster={item.thumb_url ? mediaSrc(item.thumb_url) : undefined}
          className="w-full object-cover block min-h-[140px] bg-[#0B0B0D] transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
          muted loop playsInline preload="none"
          onMouseEnter={(e) => e.target.play().catch(() => {})}
          onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
        />
      ) : (
        <img src={mediaSrc(item.thumb_url || item.url)} alt={item.ai_caption || item.title} loading="lazy"
          className="w-full object-cover block min-h-[140px] bg-[#0B0B0D] transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]" />
      )}

      {/* permanent soft bottom scrim so titles are always legible */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent opacity-80 group-hover:opacity-0 transition-opacity duration-300" />

      {item.asset_count > 1 && (
        <div data-testid={`set-badge-${item.id}`} className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-black/55 backdrop-blur-md text-white px-2 py-0.5 rounded-full border border-white/15 font-mono text-[10px]">
          <Layers size={11} /> {item.asset_count}
        </div>
      )}
      {item.media_type === "video" && !(item.asset_count > 1) && (
        <div className="absolute top-3 right-3 z-10 grid place-items-center w-8 h-8 bg-black/50 backdrop-blur-md text-white rounded-full border border-white/15 group-hover:bg-[#00E5FF] group-hover:text-black group-hover:border-[#00E5FF] transition-colors">
          <Play size={12} fill="currentColor" />
        </div>
      )}
      {item.is_private && (
        <div data-testid={`draft-badge-${item.id}`} className="absolute top-3 left-3 z-10 flex items-center gap-1 bg-[#0066FF] text-white px-2 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-wider shadow-[0_2px_12px_rgba(0,102,255,0.6)]">
          <Lock size={9} /> Draft
        </div>
      )}

      {/* caption lifts + fades in on hover */}
      <div className="absolute inset-x-0 bottom-0 z-[8] p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-400">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent -z-10" />
        {item.category && (
          <span className="inline-block mb-1.5 text-[#00E5FF] font-mono text-[9px] uppercase tracking-[0.18em]">{item.category}</span>
        )}
        <p className="text-white font-heading text-base sm:text-lg tracking-tight leading-tight line-clamp-2">{item.title}</p>
        <p className="text-white/80 text-xs font-semibold mt-1 flex items-center gap-1 min-w-0"><span className="truncate">{item.author_display_name || item.author_username}</span><VerifiedBadge show={item.author_verified} size={12} testId={`card-verified-${item.id}`} /></p>
        <p className="text-white/55 font-mono text-[10px] mb-2.5 truncate">@{item.author_username}</p>
        <div className="flex items-center gap-4 text-white/85 font-mono text-[10px]">
          <span className="flex items-center gap-1"><Heart size={11} /> {item.likes_count}</span>
          <span className="flex items-center gap-1"><MessageCircle size={11} /> {item.comments_count}</span>
          <span className="flex items-center gap-1"><Eye size={11} /> {item.views}</span>
        </div>
      </div>
    </Link>
  );
}
